import { useState, useEffect, useCallback, useRef } from 'react'
import { useSeasonWebSocket } from '@/contexts/SeasonWebSocketContext'
import { useAuth } from '@/contexts/AuthContext'
import { useFloosball } from '@/contexts/FloosballContext'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

export interface SnapshotPlayer {
  slot: string
  playerId: number
  playerName: string
  position: string
  teamAbbr: string
  teamId?: number | null
  earnedPoints: number
  weekFP: number
}

export interface TrackedStat {
  group: 'passing' | 'rushing' | 'receiving' | 'kicking' | 'returning'
  key: string
  dbKey: string
  label: string
}

export interface CardBreakdownEntry {
  // ⚠️ A synthetic is minted AT its effect's edition, so `edition` alone cannot tell a
  // built card from a pulled one — any chip showing an edition must prefer this.
  synthetic?: boolean
  slotNumber: number
  playerId: number
  playerName: string
  edition: string
  tier?: number
  effectName: string
  displayName: string
  detail: string
  /** The stat(s) this card scores off. Empty for roster-wide effects, which have no
   *  single figure to point at. Resolve against `playerGameStats` using `dbKey`. */
  trackedStats?: TrackedStat[]
  category: string
  outputType: string
  primaryFP: number
  primaryMult: number
  primaryFloobits: number
  preMatchFP: number
  preMatchFloobits: number
  preMatchMult: number
  matchMultiplied: boolean
  matchMultiplier: number
  conditionalBonus: number
  conditionalLabel: string | null
  secondaryFP: number
  secondaryFloobits: number
  secondaryMult: number
  totalFP: number
  floobitsEarned: number
  playerStatLine: string
  equation: string
  isChanceEffect?: boolean
  chanceThreshold?: number   // trigger probability (0-100) for chance cards
  chanceTriggered?: boolean  // did the chance roll fire this week
  chanceResolved?: boolean   // false while live (roll resolves at week end) → UI shows Pending
  streakActive?: boolean | null
  streakCount?: number
  // FP power-bar gate: null = ungated, true = the depicted player cleared the bar
  // (effect fired), false = bar not met (effect scored nothing this week).
  gateActive?: boolean | null
  gateThreshold?: number
  gateInverse?: boolean       // inverse gate — active WHILE under the threshold
  gateAllPro?: boolean        // All-Pro card: its bar is lowered 30%
  // Glitch (docs/GLITCH_CARDS.md). A card marked during a Criticality pays an extra
  // unpredictable bonus on top of its printed effect — it never degrades it.
  glitched?: boolean
  glitchChance?: number       // this week's trigger odds (0-1); drives the scramble intensity
  glitchTriggered?: boolean   // resolves at WEEK END — false while live means "not yet rolled"
  glitchOutcome?: string | null  // surge name; only ever SHOWN when glitchReadable
  glitchFp?: number           // extra FP the surge added
  glitchMultDelta?: number    // extra FPx delta the surge added
  glitchReadable?: boolean    // true only when the on-card player is awakened
}

export interface ChanceAmplifier {
  name: string
  bonus: number
}

export interface HandSynergies {
  chance: {
    count: number
    innateBonus: number
    amplifiers: ChanceAmplifier[]
    totalBonus: number
    hasAdvantage: boolean
    advantageRolls?: number
    catalystJackpotBoost?: number
  }
  streak: {
    count: number
    activeCount: number
  }
  match: {
    count: number
    total: number
  }
  // Team stacking: the largest same-team group fielded, how many are Champions
  // (which amplify the stack), and the lineup-wide FPx delta (0 below a 2-stack).
  stack?: {
    size: number
    champions: number
    bonus: number
  }
}

export interface EquationSummary {
  weekRawFP: number
  totalBonusFP: number
  multFactors: number[]
  handSynergies?: HandSynergies
  // When a Criticality is active, the controlling Core's signature equation
  // replaces the standard aggregator. The backend sends the resolved equation
  // string (with values) + a template; the UI shows these instead of the
  // standard (roster + FP) × FPx breakdown.
  criticalityCore?: string | null
  criticalityEquation?: string | null
  criticalityEquationTemplate?: string | null
}

export interface ModifierInfo {
  name: string
  displayName: string
  description: string
}

export interface FavoriteTeamData {
  teamId: number
  teamName: string
  teamAbbr: string
  teamColor: string
  elo: number
  record: string
  streak: number
  inPlayoffs: boolean
  wonThisWeek: boolean | null
  gameScore: string | null
}

export interface PlayerGameStats {
  passing: Record<string, number>
  rushing: Record<string, number>
  receiving: Record<string, number>
  kicking: Record<string, number>
  /** Return production, credited to the returner. Two cards score off it. */
  returning?: Record<string, number>
  fantasyPoints: number
}

export interface SnapshotEntry {
  rank: number
  userId: number
  username: string
  seasonEarnedFP: number
  seasonCardBonus: number
  seasonTotal: number
  weekPlayerFP: number
  weekCardBonus: number
  weekTotal: number
  lockedAt: string | null
  players: SnapshotPlayer[]
  cardBreakdowns: CardBreakdownEntry[]
  equationSummary?: EquationSummary
  favoriteTeamData?: FavoriteTeamData | null
  playerGameStats?: Record<number, PlayerGameStats>
}

interface SnapshotData {
  season: number | null
  week: number
  gamesActive: boolean
  entries: SnapshotEntry[]
  modifier: ModifierInfo | null
}

interface UseFantasySnapshotResult {
  entries: SnapshotEntry[]
  myEntry: SnapshotEntry | undefined
  season: number | null
  week: number
  gamesActive: boolean
  modifier: ModifierInfo | null
  loading: boolean
  refetch: () => void
}

/**
 * Fetches the fantasy snapshot from REST and keeps it updated via WS events.
 *
 * - On mount: fetches GET /api/fantasy/snapshot
 * - On leaderboard_update WS: converts legacy leaderboard format to snapshot entries
 * - On game_end / week_end / season_end / week_start: re-fetches from REST
 */
export function useFantasySnapshot(userId?: number): UseFantasySnapshotResult {
  const [entries, setEntries] = useState<SnapshotEntry[]>([])
  const [season, setSeason] = useState<number | null>(null)
  const [week, setWeek] = useState(0)
  const [gamesActive, setGamesActive] = useState(false)
  const [modifier, setModifier] = useState<ModifierInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const { event } = useSeasonWebSocket()
  const { getToken } = useAuth()
  const { seasonState } = useFloosball()
  const hasLoadedOnce = useRef(false)
  const fetchIdRef = useRef(0)
  const lastSeenWeekRef = useRef(0)
  const lastFetchTimeRef = useRef(0)

  const fetchSnapshot = useCallback(async () => {
    const isInitial = !hasLoadedOnce.current
    const fetchId = ++fetchIdRef.current
    try {
      if (isInitial) setLoading(true)
      const tok = await getToken()
      const headers: Record<string, string> = {}
      if (tok) headers['Authorization'] = `Bearer ${tok}`
      const resp = await fetch(`${API_BASE}/fantasy/snapshot`, { headers })
      const json = await resp.json()
      // Discard stale responses — only apply the most recent fetch
      if (fetchId !== fetchIdRef.current) return
      const data: SnapshotData = json.data ?? json
      setEntries(data.entries ?? [])
      setSeason(data.season ?? null)
      setWeek(data.week ?? 0)
      setGamesActive(data.gamesActive ?? false)
      setModifier(data.modifier ?? null)
      hasLoadedOnce.current = true
    } catch (err) {
      console.error('Error fetching fantasy snapshot:', err)
    } finally {
      if (isInitial) setLoading(false)
    }
  }, [getToken])

  // Initial fetch
  useEffect(() => {
    fetchSnapshot()
  }, [fetchSnapshot])

  // Re-fetch when the week changes (catches transitions missed by WS events)
  useEffect(() => {
    const serverWeek = seasonState.currentWeek
    if (serverWeek > 0 && lastSeenWeekRef.current > 0 && serverWeek !== lastSeenWeekRef.current) {
      // Week changed — clear stale weekly data and re-fetch
      setEntries(prev => prev.map(e => ({
        ...e,
        weekPlayerFP: 0,
        weekCardBonus: 0,
        weekTotal: 0,
        cardBreakdowns: [],
        equationSummary: undefined,
        players: e.players.map(p => ({ ...p, weekFP: 0 })),
      })))
      fetchSnapshot()
    }
    if (serverWeek > 0) lastSeenWeekRef.current = serverWeek
  }, [seasonState.currentWeek, fetchSnapshot])

  // Re-fetch when shop purchases happen (e.g., modifier nullifier changes effective modifier)
  useEffect(() => {
    const handler = () => fetchSnapshot()
    window.addEventListener('floosball:shop-purchase', handler)
    return () => window.removeEventListener('floosball:shop-purchase', handler)
  }, [fetchSnapshot])

  // WS-driven updates
  useEffect(() => {
    if (!event) return

    // On week_start: immediately clear previous week's card data, then re-fetch
    if (event.event === 'week_start') {
      const wsWeek = (event as any).weekNumber
      if (wsWeek != null) setWeek(wsWeek)
      // Extract modifier directly from WS event to avoid race with fetchSnapshot
      const wsModInfo = (event as any).modifierInfo
      if (wsModInfo) setModifier(wsModInfo)
      setEntries(prev => prev.map(e => ({
        ...e,
        weekPlayerFP: 0,
        weekCardBonus: 0,
        weekTotal: 0,
        cardBreakdowns: [],
        equationSummary: undefined,
        players: e.players.map(p => ({ ...p, weekFP: 0 })),
      })))
      setGamesActive(false)
      fetchSnapshot()
      return
    }

    // Re-fetch on other milestone events (debounce rapid succession)
    if (
      event.event === 'game_end' ||
      event.event === 'week_end' ||
      event.event === 'season_end'
    ) {
      const now = Date.now()
      if (now - lastFetchTimeRef.current > 2000) {
        lastFetchTimeRef.current = now
        fetchSnapshot()
      }
      return
    }

    // Live update from leaderboard_update WS event
    if (event.event === 'leaderboard_update') {
      const lb = (event as any).leaderboard
      const wsSeason = (event as any).season
      if (!Array.isArray(lb)) return

      if (wsSeason != null) setSeason(wsSeason)

      setEntries(prev => {
        // Build lookup of previous entries to preserve fields the WS event doesn't include
        const prevByUser = new Map(prev.map(p => [p.userId, p]))

        return lb.map((e: any) => {
          const existing = prevByUser.get(e.userId)
          return {
            rank: e.rank ?? 0,
            userId: e.userId,
            username: e.username,
            seasonEarnedFP: e.rawPoints ?? 0,
            seasonCardBonus: e.cardBonusPoints ?? 0,
            seasonTotal: e.totalPoints ?? 0,
            weekPlayerFP: e.weekPlayerFP ?? 0,
            weekCardBonus: e.weekCardBonus ?? 0,
            weekTotal: Math.round(((e.weekPlayerFP ?? 0) + (e.weekCardBonus ?? 0)) * 10) / 10,
            lockedAt: e.lockedAt ?? null,
            players: (e.players ?? []).map((p: any) => ({
              slot: p.slot,
              playerId: p.playerId,
              playerName: p.playerName,
              position: p.position,
              teamAbbr: p.teamAbbr,
              teamId: p.teamId ?? null,
              earnedPoints: p.earnedPoints ?? 0,
              weekFP: p.weekFP ?? 0,
            })),
            cardBreakdowns: (e.cardBreakdowns ?? []).map((cb: any) => ({
              slotNumber: cb.slotNumber ?? 0,
              playerId: cb.playerId ?? 0,
              playerName: cb.playerName ?? '',
              edition: cb.edition ?? 'metallic',
              tier: cb.tier ?? 1,
              effectName: cb.effectName ?? '',
              displayName: cb.displayName ?? '',
              detail: cb.detail ?? '',
              category: cb.category ?? '',
              outputType: cb.outputType ?? 'fp',
              primaryFP: cb.primaryFP ?? 0,
              primaryMult: cb.primaryMult ?? 0,
              primaryFloobits: cb.primaryFloobits ?? 0,
              preMatchFP: cb.preMatchFP ?? 0,
              preMatchFloobits: cb.preMatchFloobits ?? 0,
              preMatchMult: cb.preMatchMult ?? 0,
              matchMultiplied: cb.matchMultiplied ?? false,
              matchMultiplier: cb.matchMultiplier ?? 1.5,
              conditionalBonus: cb.conditionalBonus ?? 0,
              conditionalLabel: cb.conditionalLabel ?? null,
              secondaryFP: cb.secondaryFP ?? 0,
              secondaryFloobits: cb.secondaryFloobits ?? 0,
              secondaryMult: cb.secondaryMult ?? 0,
              totalFP: cb.totalFP ?? 0,
              floobitsEarned: cb.floobitsEarned ?? 0,
              playerStatLine: cb.playerStatLine ?? '',
              equation: cb.equation ?? '',
              isChanceEffect: cb.isChanceEffect ?? false,
              chanceThreshold: cb.chanceThreshold ?? 0,
              chanceTriggered: cb.chanceTriggered ?? false,
              chanceResolved: cb.chanceResolved ?? false,
              streakActive: cb.streakActive ?? null,
              streakCount: cb.streakCount ?? 0,
              gateActive: cb.gateActive ?? null,
              gateThreshold: cb.gateThreshold ?? 0,
              gateInverse: cb.gateInverse ?? false,
              gateAllPro: cb.gateAllPro ?? false,
              glitched: cb.glitched ?? false,
              glitchChance: cb.glitchChance ?? 0,
              glitchTriggered: cb.glitchTriggered ?? false,
              glitchOutcome: cb.glitchOutcome ?? null,
              glitchFp: cb.glitchFp ?? 0,
              glitchMultDelta: cb.glitchMultDelta ?? 0,
              glitchReadable: cb.glitchReadable ?? false,
            })),
            equationSummary: e.equationSummary ?? existing?.equationSummary ?? undefined,
            favoriteTeamData: e.favoriteTeamData ?? existing?.favoriteTeamData ?? null,
            playerGameStats: e.playerGameStats ?? existing?.playerGameStats ?? {},
          }
        })
      })
      setGamesActive(true)
      setLoading(false)
    }
  }, [event, fetchSnapshot])

  const myEntry = userId != null ? entries.find(e => e.userId === userId) : undefined

  return { entries, myEntry, season, week, gamesActive, modifier, loading, refetch: fetchSnapshot }
}
